/*
 * Copyright 2026 OpenTrace Contributors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Deterministic color assignment for node types.
 *
 * Reads from CSS variables (--graph-node-<type>, --graph-node-palette-N)
 * so themes can override graph colors. Falls back to hardcoded defaults
 * when CSS variables aren't available (e.g. tests, SSR, Node).
 */

const PALETTE_SIZE = 12;

const FALLBACK_PALETTE = [
  '#6366f1', // Indigo
  '#eab308', // Amber
  '#22c55e', // Green
  '#a855f7', // Violet
  '#f43f5e', // Rose
  '#3b82f6', // Blue
  '#ec4899', // Pink
  '#14b8a6', // Teal
  '#f97316', // Orange
  '#84cc16', // Lime
  '#d946ef', // Fuchsia
  '#06b6d4', // Cyan
];

/** Well-known node types → fallback colors (used when no CSS variable set) */
const FALLBACK_KNOWN: Record<string, string> = {
  Repository: '#22c55e',
  Class: '#3b82f6',
  Function: '#a855f7',
  File: '#84cc16',
  Directory: '#06b6d4',
  Variable: '#f59e0b',
  // Dependency is a first-class type themes may color (--graph-node-dependency,
  // e.g. the brand palette). Without it here, getNodeColor never reads that var
  // and Dependency nodes silently hash into the palette instead — and the
  // legend (same function) would disagree with a theme that sets the var.
  Dependency: '#3d97d4',
  // KnowledgeDoc/KnowledgeVault would otherwise hash into the palette like any
  // unnamed type — on themes with a narrow hue spread (e.g. 'clean', whose
  // unnamed-type slots are a teal/orange/gold ladder) two of those slots can
  // land on near-identical teals, making the two most common vault node types
  // visually indistinguishable. Pinned distinct colors, same rationale as
  // Dependency above.
  KnowledgeVault: '#f97316',
  KnowledgeDoc: '#ec4899',
};

function djb2(str: string): number {
  let h = 5381;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) + h + str.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

// ─── Theme-aware color cache ────────────────────────────────────────────

interface ColorCache {
  themeKey: string;
  known: Map<string, string>;
  palette: string[];
}

let cache: ColorCache | null = null;

function resolveColors(): ColorCache {
  if (typeof document === 'undefined') {
    // Non-browser (tests, SSR) — use fallbacks
    return {
      themeKey: '__fallback__',
      known: new Map(Object.entries(FALLBACK_KNOWN)),
      palette: FALLBACK_PALETTE,
    };
  }

  const root = document.documentElement;
  const themeKey = `${root.dataset.theme ?? ''}_${root.dataset.mode ?? ''}`;
  if (cache && cache.themeKey === themeKey) return cache;

  const style = getComputedStyle(root);

  // Read known type colors
  const known = new Map<string, string>();
  for (const [type, fallback] of Object.entries(FALLBACK_KNOWN)) {
    const varName = `--graph-node-${type.toLowerCase()}`;
    const val = style.getPropertyValue(varName).trim();
    known.set(type, val || fallback);
  }

  // Read palette
  const palette: string[] = [];
  for (let i = 0; i < PALETTE_SIZE; i++) {
    const val = style.getPropertyValue(`--graph-node-palette-${i}`).trim();
    palette.push(val || FALLBACK_PALETTE[i]);
  }

  cache = { themeKey, known, palette };
  return cache;
}

export function getNodeColor(type: string): string {
  const { known, palette } = resolveColors();
  return known.get(type) ?? palette[djb2(type) % palette.length];
}
